import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { getFeaturedProducts } from '../services/products';
import { 
  FaShieldAlt, 
  FaStar, 
  FaCalendarAlt,
  FaCreditCard,
  FaTruck,
  FaUsers,
  FaHeart,
  FaBroom,
  FaClock,
  FaSmile
} from 'react-icons/fa';
import Hero from '../components/Hero';
import SocialProof from '../components/SocialProof';
// Testing components - temporarily disabled
// import FirebaseTest from '../components/FirebaseTest';
// import AppCheckTest from '../components/AppCheckTest';
// import RouteTest from '../components/RouteTest';
// import ProductDetailTest from '../components/ProductDetailTest';
// import TestChecklist from '../components/TestChecklist';
// import AdminLogin from './admin/Login';

import { useScrollAnimation } from '../hooks/useScrollAnimation';

const HomeContainer = styled.div`
  min-height: 100vh;
  padding-top: 32px;
`;



const FeaturesSection = styled.section`
  padding: ${({ theme }) => theme.spacing[16]} 0;
  background: ${({ theme }) => theme.colors.background.primary};
  position: relative;
  overflow: hidden;
  
  /* Decorative blobs */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -20px;
    width: 256px;
    height: 256px;
    background: ${({ theme }) => theme.colors.yellow[200]};
    border-radius: 50%;
    opacity: 0.5;
    filter: blur(32px);
    z-index: -1;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    right: -20px;
    width: 288px;
    height: 288px;
    background: ${({ theme }) => theme.colors.pink[200]};
    border-radius: 50%;
    opacity: 0.5;
    filter: blur(32px);
    z-index: -1;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: ${({ theme }) => theme.typography.fontSize['4xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: #39ff14;
  margin-bottom: ${({ theme }) => theme.spacing[12]};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  text-shadow: 
    0 0 20px rgba(57, 255, 20, 0.8),
    0 0 40px rgba(57, 255, 20, 0.6),
    0 0 60px rgba(57, 255, 20, 0.4);

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  }
`;

const FeaturesGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing[4]};
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing[8]};
`;

const FeatureCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.background.primary};
  padding: ${({ theme }) => theme.spacing[8]};
  border-radius: ${({ theme }) => theme.radii['2xl']};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  text-align: center;
  border: none;
  transition: all ${({ theme }) => theme.transitions.normal};
  transform: translateY(0);

  &:hover {
    transform: translateY(-8px);
    box-shadow: ${({ theme }) => theme.shadows.xl};
  }
`;

const FeatureIcon = styled.div`
  width: 64px;
  height: 64px;
  background: ${({ theme, color }) => theme.colors[color]?.[100] || theme.colors.orange[100]};
  border-radius: ${({ theme }) => theme.radii.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${({ theme }) => theme.spacing[6]};
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  color: ${({ theme, color }) => theme.colors[color]?.[500] || theme.colors.primary};
`;

const FeatureTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
`;

const FeatureDescription = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
`;

const StatsSection = styled.section`
  background: ${({ theme }) => theme.colors.background.accent};
  padding: ${({ theme }) => theme.spacing[16]} 0;
`;

const StatsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing[4]};
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing[8]};
  text-align: center;
`;

const StatCard = styled(motion.div)`
  background: #11162b;
  border: 2px solid rgba(57, 255, 20, 0.3);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 
    0 0 20px rgba(57, 255, 20, 0.3),
    0 0 40px rgba(57, 255, 20, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 
      0 0 30px rgba(57, 255, 20, 0.5),
      0 0 60px rgba(57, 255, 20, 0.2);
    border-color: rgba(57, 255, 20, 0.6);
  }

  h3 {
    font-size: ${({ theme }) => theme.typography.fontSize['4xl']};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: #39ff14;
    margin-bottom: ${({ theme }) => theme.spacing[2]};
    text-shadow: 
      0 0 15px rgba(57, 255, 20, 0.8),
      0 0 30px rgba(57, 255, 20, 0.4);
  }

  p {
    color: #b0eaff;
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    text-shadow: 0 0 8px rgba(176, 234, 255, 0.6);
  }
`;

const TestimonialsSection = styled.section`
  padding: ${({ theme }) => theme.spacing[16]} 0;
  background: ${({ theme }) => theme.colors.background.primary};
`;

const TestimonialsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing[4]};
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: ${({ theme }) => theme.spacing[6]};
`;

const TestimonialCard = styled(motion.div)`
  background: #11162b;
  padding: ${({ theme }) => theme.spacing[6]};
  border-radius: ${({ theme }) => theme.radii.xl};
  border: 2px solid rgba(0, 204, 255, 0.3);
  box-shadow: 
    0 0 20px rgba(0, 204, 255, 0.3),
    0 0 40px rgba(0, 204, 255, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 
      0 0 30px rgba(0, 204, 255, 0.5),
      0 0 60px rgba(0, 204, 255, 0.2);
    border-color: rgba(0, 204, 255, 0.6);
  }
`;

const TestimonialText = styled.p`
  font-style: italic;
  color: #ffffff;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  text-shadow: 0 0 8px rgba(255, 255, 255, 0.6);
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const AuthorAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #39ff14, #00ccff);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0a0f1c;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  box-shadow: 0 0 15px rgba(57, 255, 20, 0.6);
  border: 2px solid rgba(57, 255, 20, 0.4);
`;

const AuthorInfo = styled.div`
  h4 {
    color: #ffffff;
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    margin-bottom: ${({ theme }) => theme.spacing[1]};
    text-shadow: 0 0 8px rgba(255, 255, 255, 0.6);
  }

  p {
    color: #b0eaff;
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    text-shadow: 0 0 6px rgba(176, 234, 255, 0.6);
  }
`;

const Stars = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
  color: #00ccff;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  text-shadow: 0 0 8px rgba(0, 204, 255, 0.8);
`;

const Home = () => {
  useScrollAnimation();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        const products = await getFeaturedProducts(6);
        setFeaturedProducts(products);
      } catch (error) {
        console.error('Error fetching featured products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedProducts();
  }, []);
  
  const features = [
    {
      icon: FaShieldAlt,
      title: 'Safety First',
      description: 'All our bounce houses are safety certified and regularly inspected for your peace of mind.',
      color: 'blue'
    },
    {
      icon: FaTruck,
      title: 'Professional Setup',
      description: 'Our experienced team handles delivery, setup, and cleanup so you can focus on having fun.',
      color: 'green'
    },
    {
      icon: FaCalendarAlt,
      title: 'Easy Booking',
      description: 'Book your bounce house online in minutes with our simple and secure booking system.',
      color: 'pink'
    },
    {
      icon: FaCreditCard,
      title: 'Secure Payments',
      description: 'Safe and secure payment processing with multiple payment options available.',
      color: 'purple'
    },
    {
      icon: FaUsers,
      title: 'All Ages Welcome',
      description: 'From toddlers to adults, we have bounce houses perfect for every age group.',
      color: 'yellow'
    },
    {
      icon: FaHeart,
      title: 'Customer Satisfaction',
      description: 'We\'re committed to making your event special with exceptional service and support.',
      color: 'red'
    }
  ];

  const testimonials = [
    {
      text: "The bounce house was a huge hit at my daughter's birthday party! The setup was quick and the kids had a blast. Highly recommend!",
      author: "Sarah Johnson",
      role: "Happy Parent",
      rating: 5
    },
    {
      text: "Professional service from start to finish. The equipment was clean, safe, and the staff was friendly and helpful.",
      author: "Mike Chen",
      role: "Event Organizer",
      rating: 5
    },
    {
      text: "Best bounce house rental in town! Great prices, excellent service, and the kids absolutely loved it. Will definitely book again!",
      author: "Emily Rodriguez",
      role: "Satisfied Customer",
      rating: 5
    }
  ];

  return (
    <HomeContainer>
      <Hero />

      {/* Featured Inflatables Section - exactly matching the image */}
      <section className="featured" style={{
        textAlign: 'center',
        padding: '40px 20px',
        background: '#0a0f1c'
      }}>
        <h2 style={{
          fontSize: '2rem',
          color: '#39ff14',
          textShadow: '0 0 15px rgba(57, 255, 20, 0.7)',
          marginBottom: '3rem',
          animation: 'neon-glow 2s ease-in-out infinite alternate'
        }}>
          Featured Inflatables
        </h2>
        
        <div className="cards" style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '20px',
          flexWrap: 'wrap',
          marginTop: '30px',
          maxWidth: '1200px',
          marginLeft: 'auto',
          marginRight: 'auto',
          padding: '0 1rem'
        }}>
          {loading ? (
            // Loading state
            <div style={{ color: '#b0eaff', fontSize: '18px', textAlign: 'center', width: '100%' }}>
              Loading featured products...
            </div>
          ) : featuredProducts.length > 0 ? (
            // Featured products from database
            featuredProducts.map((product, index) => (
              <div key={product.id} className="card" style={{
                background: '#11162b',
                borderRadius: '15px',
                padding: '20px',
                textAlign: 'center',
                width: '250px',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                position: 'relative',
                border: '1px solid rgba(255, 128, 0, 0.3)',
                boxShadow: '0 0 20px rgba(255, 128, 0, 0.5)',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-5px)';
                e.target.style.boxShadow = '0 0 30px rgba(255, 128, 0, 0.7)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 0 20px rgba(255, 128, 0, 0.5)';
              }}
              onClick={() => window.location.href = `/products/${product.id}`}
              >
                {product.categories?.includes('popular') && (
                  <div className="badge" style={{
                    position: 'absolute',
                    top: '1rem',
                    left: '1rem',
                    background: '#ff8000',
                    color: '#0a0f1c',
                    padding: '0.5rem 1rem',
                    borderRadius: '20px',
                    fontWeight: 'bold',
                    fontSize: '0.8rem',
                    zIndex: 1
                  }}>
                    Most Popular
                  </div>
                )}
                <img 
                  src={product.images?.[0] || '/images/placeholder.svg'} 
                  alt={product.title} 
                  style={{
                    width: '100%',
                    height: '200px',
                    objectFit: 'cover',
                    borderRadius: '10px'
                  }} 
                />
                <h3 style={{color: '#ffffff', marginBottom: '10px', fontSize: '1.3rem', fontWeight: 'bold'}}>
                  {product.title}
                </h3>
                <p style={{color: '#39ff14', fontSize: '1.2rem', margin: '10px 0', fontWeight: 'bold'}}>
                  ${product.price} / day
                </p>
                <div className="card-buttons" style={{
                  display: 'flex',
                  gap: '1rem',
                  padding: '1rem',
                  justifyContent: 'center'
                }}>
                  <button className="btn" style={{
                    background: 'linear-gradient(90deg, #00ccff, #00e6e6)',
                    color: '#0a0f1c',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '25px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    flex: 1,
                    minHeight: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    whiteSpace: 'nowrap'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.location.href = `/booking?product=${product.id}`;
                  }}
                  aria-label={`Reserve ${product.title}`}
                  >
                    Reserve
                  </button>
                  <button className="btn secondary" style={{
                    background: 'transparent',
                    color: '#ffffff',
                    border: '2px solid #b0eaff',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '25px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    flex: 1,
                    minHeight: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    whiteSpace: 'nowrap'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.location.href = `/products/${product.id}`;
                  }}
                  aria-label={`View details for ${product.title}`}
                  >
                    Details
                  </button>
                </div>
              </div>
            ))
          ) : (
            // No featured products
            <div style={{ color: '#b0eaff', fontSize: '18px', textAlign: 'center', width: '100%' }}>
              No featured products available. Check back soon!
            </div>
          )}


        </div>
      </section>
      
      {/* Testing components - temporarily disabled due to theme errors */}
      {/* <TestChecklist />
      <AdminLogin />
      <FirebaseTest />
      <AppCheckTest />
      <RouteTest />
      <ProductDetailTest /> */}
      <SocialProof />
      
      <div className="container" style={{
        display: "grid", 
        gap: "20px", 
        gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", 
        marginTop: "40px",
        marginBottom: "40px",
        maxWidth: "1200px",
        marginLeft: "auto",
        marginRight: "auto",
        padding: "0 20px"
      }}>
        {[
          {icon: <FaClock style={{fontSize: "24px", color: "#00ccff"}}/>, label: "Free Overnight"},
          {icon: <FaBroom style={{fontSize: "24px", color: "#00ccff"}}/>, label: "Sanitized After Every Rental"},
          {icon: <FaShieldAlt style={{fontSize: "24px", color: "#00ccff"}}/>, label: "Fully Insured"},
          {icon: <FaSmile style={{fontSize: "24px", color: "#00ccff"}}/>, label: "Friendly Setup Crew"}
        ].map((item, idx) => (
          <div key={idx} style={{
            display: "flex",
            gap: "15px",
            alignItems: "center",
            background: "#11162b",
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 0 20px rgba(0, 204, 255, 0.3)",
            border: "1px solid rgba(0, 204, 255, 0.3)",
            transition: "all 0.3s ease"
          }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "40px",
              height: "40px",
              borderRadius: "8px",
              background: "rgba(0, 204, 255, 0.1)",
              border: "1px solid rgba(0, 204, 255, 0.3)"
            }}>
              {item.icon}
            </div>
            <span style={{
              fontWeight: "600",
              color: "#00ccff",
              fontSize: "16px",
              textShadow: "0 0 5px rgba(0, 204, 255, 0.5)"
            }}>
              {item.label}
            </span>
          </div>
        ))}
      </div>
      
      <div style={{ padding: '64px 0' }}>
        <FeaturesSection className="reveal">
          <SectionTitle>Why Choose InflatableTx?</SectionTitle>
          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <FeatureIcon color={feature.color}>
                  <feature.icon />
                </FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </FeaturesSection>

        <StatsSection>
          <SectionTitle>Our Success in Numbers</SectionTitle>
          <StatsGrid>
            <StatCard
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3>500+</h3>
              <p>Happy Events</p>
            </StatCard>
            <StatCard
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3>50+</h3>
              <p>Bounce Houses</p>
            </StatCard>
            <StatCard
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3>100%</h3>
              <p>Safety Record</p>
            </StatCard>
            <StatCard
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3>24/7</h3>
              <p>Support</p>
            </StatCard>
          </StatsGrid>
        </StatsSection>

        <TestimonialsSection>
          <SectionTitle>What Our Customers Say</SectionTitle>
          <TestimonialsGrid>
            {testimonials.map((testimonial, index) => (
              <TestimonialCard
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <Stars>
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} />
                  ))}
                </Stars>
                <TestimonialText>"{testimonial.text}"</TestimonialText>
                <TestimonialAuthor>
                  <AuthorAvatar>
                    {testimonial.author.charAt(0)}
                  </AuthorAvatar>
                  <AuthorInfo>
                    <h4>{testimonial.author}</h4>
                    <p>{testimonial.role}</p>
                  </AuthorInfo>
                </TestimonialAuthor>
              </TestimonialCard>
            ))}
          </TestimonialsGrid>
        </TestimonialsSection>
      </div>
    </HomeContainer>
  );
};

export default Home;
